import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenManifest } from '@/shared/screen/types';

import { PERSON_KINDS, personListPath, type PersonKind } from './person.kind';

// Manifestos do registro de PF e PJ (U23/U42). As abas por caminho do legado viram `?tab=` (S29), na
// ordem do legado (NaturalPersonForm.tsx:89-98, LegalPersonForm.tsx:79-89); as condicionais saem por
// `hidden` na tela. As ações de registro com efeito (Salvar, Clonar, Atribuir) ficam na tela, com o
// formulário e a prévia de cada diálogo; o agente só navega e relê (U24).

export const NATURAL_PERSON_TABS = [
  { id: 'informacoes', label: msg`Informações` },
  { id: 'dados-bancarios', label: msg`Dados bancários` },
  { id: 'documentos', label: msg`Documentos` },
  { id: 'compliance', label: msg`Compliance` },
  { id: 'credito', label: msg`Crédito` },
  { id: 'operacoes', label: msg`Operações` },
] as const;

export const LEGAL_PERSON_TABS = [...NATURAL_PERSON_TABS, { id: 'signatarios', label: msg`Signatários` }] as const;

export type PersonTab = (typeof LEGAL_PERSON_TABS)[number]['id'];
export const PERSON_TAB_IDS = LEGAL_PERSON_TABS.map((tab) => tab.id) as [PersonTab, ...PersonTab[]];
/** Abas que o legado tinha como caminho (`router.tsx:853-891`, `:934-981`): viram redirect para `?tab=`. */
export const PERSON_PATH_TABS = PERSON_TAB_IDS.filter((tab) => tab !== 'informacoes');

const sheet = z.string().min(1).max(40).optional().catch(undefined);

/** `?tab=` e o sheet aberto (envolvido, documento, conta…). Leve, para a rota. */
export const personDetailSearch = z.object({
  tab: z.enum(PERSON_TAB_IDS).optional().catch(undefined),
  sheet,
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  size: z.coerce.number().int().refine((value) => [5, 10, 15, 20].includes(value)).optional().catch(undefined),
});
export type PersonDetailSearch = z.infer<typeof personDetailSearch>;

/** A lupa da aba Operações (`OperationList.tsx:138-142`): a operação no módulo de Crédito. */
export const personOpenOperation = action({
  id: 'person.operation.open',
  kind: 'navigate',
  page: true,
  row: true,
  label: msg`Ver operação`,
  description: msg`Abre uma operação de crédito desta pessoa na tela da operação, pelo id da linha da aba Operações.`,
  input: z.object({ id: z.string().min(1) }),
  run: ({ nav, input }) => nav.to(`/ccb/operacoes/${encodeURIComponent(input.id)}`),
});

/** Página da aba Operações (o legado guarda em estado; aqui na URL, U39 §2). */
export const personOperationsPage = action({
  id: 'person.operations.page',
  kind: 'navigate',
  page: true,
  label: msg`Mudar de página`,
  description: msg`Muda a página (começa em 0) ou a quantidade de linhas por página da aba Operações.`,
  input: z.object({ page: z.number().int().min(0), size: z.number().int().min(1).max(100) }),
  run: ({ nav, input }) => nav.search({ page: input.page > 0 ? input.page + 1 : undefined, size: input.size === 5 ? undefined : input.size }),
});

/* eslint-disable lingui/no-unlocalized-strings -- resposta ao modelo, inglês canônico (U33) */
export const OPEN_PERSON_ONLY = 'This action only runs on the person record screen, on the record that is open there.';
/* eslint-enable lingui/no-unlocalized-strings */
const needsOpenPerson = () => {
  throw new Error(OPEN_PERSON_ONLY);
};

/** "Regularizar CPF" (NaturalPersonForm.tsx:175-180). A tela prende ao registro aberto (`person-detail-agent.ts`). */
export const personRegulate = action({
  id: 'person.naturalPerson.regulate',
  kind: 'write',
  label: msg`Regularizar CPF`,
  description: msg`Regulariza o CPF da pessoa física aberta na tela. Não recebe entrada: a pessoa é sempre a aberta e não pode ser escolhida; para regularizar outra, abra-a antes. Só no correspondente raiz, com permissão de consultar dados. Pede a confirmação da pessoa.`,
  input: z.strictObject({}),
  preview: () => ({ title: i18n._(msg`Regularizar CPF`), detail: i18n._(msg`O CPF da pessoa aberta na tela é regularizado.`) }),
  run: needsOpenPerson,
});

/** Quem recebe o acesso no "Compartilhar registro" (ShareRecord.tsx:22-56): tipo, o registro dele e o nível. */
export const personShareInput = z.strictObject({
  principalType: z.enum(['User', 'Group', 'Tenant']),
  principalId: z.string().min(1),
  permissionLevelType: z.enum(['View', 'Edit']),
});

/** "Compartilhar registro" (NaturalPersonForm.tsx:181-186, LegalPersonForm.tsx:167-172). */
export const personShare = action<z.infer<typeof personShareInput>>({
  id: 'person.share',
  kind: 'write',
  label: msg`Compartilhar registro`,
  description: msg`Dá acesso ao cadastro aberto na tela a um usuário (principalType User e o id do operador), a um grupo (Group e o nome do grupo) ou a um correspondente (Tenant e o id dele), para ver (View) ou editar (Edit). O cadastro é sempre o aberto e não pode ser escolhido. Pede a confirmação da pessoa.`,
  input: personShareInput,
  preview: () => ({ title: i18n._(msg`Compartilhar registro`), detail: i18n._(msg`O cadastro aberto passa a ser visto por quem você escolher.`) }),
  run: needsOpenPerson,
});

function detailScreen(kind: PersonKind): ScreenManifest {
  const natural = kind === 'natural';
  const prefix = `person.${natural ? 'naturalPerson' : 'legalPerson'}`;
  const tabs = natural ? NATURAL_PERSON_TABS : LEGAL_PERSON_TABS;
  const ids = tabs.map((tab) => tab.id) as [PersonTab, ...PersonTab[]];
  const tabList = ids.join(', ');
  return defineScreen({
    id: `${prefix}Detail`,
    path: `${personListPath(kind)}/$personId`,
    title: natural ? msg`Pessoa física` : msg`Pessoa jurídica`,
    description: natural
      ? msg`Cadastro de uma pessoa física: formulário de informações, dados bancários, documentos, consultas de compliance e de crédito e operações.`
      : msg`Cadastro de uma pessoa jurídica: formulário de informações, dados bancários, documentos, consultas de compliance e de crédito, operações e signatários.`,
    permission: can(PERSON_KINDS[kind].resource, 'Read'),
    access: { flag: { key: PERSON_KINDS[kind].menuFlag, notEquals: 0 } },
    // eslint-disable-next-line lingui/no-unlocalized-strings -- id da tela-mãe, não texto de UI
    place: { icon: natural ? 'user' : 'building-2', parent: `${prefix}List`, record: natural ? msg`Pessoa física` : msg`Pessoa jurídica`, tabs },
    actions: [
      action({
        id: `${prefix}.back`,
        kind: 'navigate',
        label: natural ? msg`Voltar para Pessoas Físicas` : msg`Voltar para Pessoas Jurídicas`,
        description: natural ? msg`Volta para a lista de pessoas físicas.` : msg`Volta para a lista de pessoas jurídicas.`,
        run: ({ nav }) => nav.to(personListPath(kind)),
      }),
      action({
        id: `${prefix}.tab`,
        kind: 'navigate',
        label: msg`Trocar de aba`,
        description: msg`Troca a aba do cadastro aberto. Abas: ${tabList}.`,
        input: z.object({ tab: z.enum(ids) }),
        run: ({ nav, input }) => nav.search({ tab: input.tab === 'informacoes' ? undefined : input.tab }),
      }),
      personOpenOperation,
      personOperationsPage,
      ...(natural ? [personRegulate] : []),
      personShare,
    ],
  });
}

export const naturalPersonDetailScreen = detailScreen('natural');
export const legalPersonDetailScreen = detailScreen('legal');
export const personDetailScreen = (kind: PersonKind) => (kind === 'natural' ? naturalPersonDetailScreen : legalPersonDetailScreen);
